import { refreshTokenApi } from "@/api/services/admin/auth";
import { ConfirmReloadModal } from "@/components/commons/ConfirmReloadModal";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { useDialog } from "@/hooks/useDialog";
import { AdminRoutes } from "@/routes/config";
import { clientApi, isAuth, setAxiosAuth } from "@/utils/api";
import { ADMIN_EMAIL_KEY, ADMIN_REFRESH_TOKEN_KEY, ADMIN_TOKEN_KEY } from "@/utils/constants";
import { setLastAuthRoute } from "@/utils/lastAuthRoute";
import { redirectToLogin } from "@/utils/url";
import { Typography } from "@mui/material";
import Cookies from "js-cookie";
import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const withAuth: <CP extends {}>(WrappedComponent: FC<CP>) => FC<CP> = (WrappedComponent) => (props) => {
  const { pathname, search } = useLocation();
  const [isVerified, setIsVerified] = useState(false);
  const userEmailRef = useRef(Cookies.get(ADMIN_EMAIL_KEY));
  const navigate = useNavigate();
  const confirmReload = useDialog();

  useEffect(() => {
    if (isAuth()) {
      setLastAuthRoute(`${pathname}${search}`);
    }
  }, [pathname, search]);

  useEffect(() => {
    // Every 2 seconds check the identity cookie for account changes
    const intervalId = setInterval(() => {
      const currentEmail = Cookies.get(ADMIN_EMAIL_KEY);
      if (!userEmailRef.current) return;
      if (!currentEmail) {
        // Session was cleared elsewhere (logout in another tab, cookie expiry).
        redirectToLogin();
      } else if (currentEmail !== userEmailRef.current) {
        // Signed in as a different account → restart from home.
        location.href = "/";
      }
    }, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      // set axios auth at init
      if (isAuth() && !clientApi.defaults.headers.common.Authorization) {
        setAxiosAuth();
      }

      // verify admin routes
      if (!isAuth() && AdminRoutes.some((path) => pathname.startsWith(path))) {
        const accessToken = Cookies.get(ADMIN_TOKEN_KEY);
        const refreshToken = Cookies.get(ADMIN_REFRESH_TOKEN_KEY);

        // Attempt token refresh before redirecting to login
        if (accessToken && refreshToken) {
          try {
            await refreshTokenApi({ accessToken, refreshToken });
            setAxiosAuth();
            // Sync identity ref after successful refresh (account may have changed via center auth)
            userEmailRef.current = Cookies.get(ADMIN_EMAIL_KEY);
            setIsVerified(true);
            return;
          } catch {
            // fall through to the logout + redirect below
          }
        }

        // Tokens are missing or refresh failed → redirectToLogin() clears the
        // invalid session itself (so the next mount doesn't re-attempt a doomed
        // refresh); calling logout() first would wipe the admin-email cookie
        // before it can be carried to /login.
        redirectToLogin();
        return;
      }

      // Sync identity ref (account may have changed via center auth)
      userEmailRef.current = Cookies.get(ADMIN_EMAIL_KEY);
      setIsVerified(true);
    };

    verifyAuth();

    // check version build
    const checkVersion = async () => {
      try {
        const response = await fetch("/version.json", {
          headers: { "Cache-Control": "no-cache" },
        });
        const { version } = await response.json();
        const currentVersion = localStorage.getItem("app-version");

        // show modal and update version
        if (version && (!currentVersion || version !== parseInt(currentVersion))) {
          localStorage.setItem("app-version", version);
          const agree = await confirmReload.show();
          if (agree) {
            window.location.reload();
          }
        }
      } catch (error) {
        console.error("Error checking version:", error);
      }
    };

    checkVersion();
  }, [pathname, navigate]);

  if (!isVerified) return <LoadingScreen />;

  return (
    <>
      <ConfirmReloadModal open={confirmReload.open} onOk={confirmReload.confirm}>
        <Typography>システムがアップデートされました。最新の状態にするため、ページをリロードしてください。</Typography>
      </ConfirmReloadModal>
      <WrappedComponent {...props} />
    </>
  );
};
